import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, ShieldCheck, Truck, Globe2, Wrench, ArrowRight } from "lucide-react";
import logoAsset from "@/assets/fixhen-logo.png.asset.json";
import { toast } from "sonner";

const searchSchema = z.object({
  mode: z.enum(["signin", "signup"]).optional(),
  redirect: z
    .string()
    .optional()
    .refine((r) => !r || (r.startsWith("/") && !r.startsWith("//")), {
      message: "Invalid redirect",
    }),
});

function safeRedirect(r: string | undefined): string {
  return r && r.startsWith("/") && !r.startsWith("//") ? r : "/account";
}

export const Route = createFileRoute("/auth")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Sign in or create an account — FIXHEN" },
      {
        name: "description",
        content:
          "Sign in to your FIXHEN account to track orders, save addresses and reorder OEM-matched parts faster.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AuthPage,
});

function passwordScore(p: string) {
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^\w\s]/.test(p)) s++;
  return Math.min(s, 4);
}

function AuthPage() {
  const search = useSearch({ from: "/auth" });
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">(search.mode ?? "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);

  useEffect(() => {
    // If already signed in, bounce to redirect target
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: safeRedirect(search.redirect) });
    });
  }, []);

  const score = passwordScore(password);
  const scoreLabels = ["Too weak", "Weak", "OK", "Strong", "Excellent"];
  const scoreColors = ["bg-rose-500", "bg-orange-500", "bg-amber-500", "bg-emerald-500", "bg-emerald-600"];

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (mode === "signup") {
        if (password.length < 8) throw new Error("Password must be at least 8 characters");
        const redirectQS = search.redirect ? `?redirect=${encodeURIComponent(search.redirect)}` : "";
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/verify-email${redirectQS}`,
            data: { first_name: firstName.trim(), last_name: lastName.trim() },
          },
        });
        if (error) throw error;
        // Supabase returns a user but NO session when confirmations are required.
        if (data.session) {
          // Auto-confirm is on (shouldn't happen in prod) — go straight in.
          try { await supabase.rpc("claim_my_orders"); } catch {}
          navigate({ to: safeRedirect(search.redirect) });
        } else {
          toast.success("Account created — check your email to confirm.");
          navigate({
            to: "/verify-email",
            search: { email: email.trim(), redirect: search.redirect } as never,
          });
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        if (error) {
          const code = (error as any).code ?? "";
          const msg = (error.message ?? "").toLowerCase();
          if (code === "email_not_confirmed" || msg.includes("not confirmed") || msg.includes("email not confirmed")) {
            // Attempt to (re)send confirmation, then land on the verify screen.
            try {
              await supabase.auth.resend({
                type: "signup",
                email: email.trim(),
                options: {
                  emailRedirectTo: `${window.location.origin}/verify-email${
                    search.redirect ? `?redirect=${encodeURIComponent(search.redirect)}` : ""
                  }`,
                },
              });
            } catch {}
            navigate({
              to: "/verify-email",
              search: { email: email.trim(), redirect: search.redirect } as never,
            });
            return;
          }
          throw error;
        }
        try { await supabase.rpc("claim_my_orders"); } catch {}
        navigate({ to: safeRedirect(search.redirect) });
      }
    } catch (e: any) {
      setErr(e?.message ?? "Something went wrong");
    } finally {
      setBusy(false);
    }
  }


  if (forgotOpen) return <ForgotPasswordCard onBack={() => setForgotOpen(false)} />;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="grid min-h-screen lg:grid-cols-2">
        {/* Brand panel */}
        <aside className="relative hidden overflow-hidden bg-gradient-to-br from-[#172554] via-[#1e3a8a] to-[#2563EB] lg:flex">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(125,211,252,0.25),transparent_60%)]" />
          <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full border border-white/10 animate-spin-slow" />
          <div className="absolute -left-16 bottom-24 h-64 w-64 rounded-full border border-white/10 animate-spin-slow" style={{ animationDirection: "reverse" }} />
          <div className="relative z-10 flex w-full flex-col justify-between p-12 text-white">
            <Link to="/" className="flex items-center gap-3">
              <img src={logoAsset.url} alt="FIXHEN" width={44} height={44} className="h-11 w-11 rounded-lg bg-white/10 p-1.5" />
              <span className="text-2xl font-black tracking-tight">FIXHEN</span>
            </Link>
            <div>
              <h2 className="text-4xl font-bold leading-tight">
                Your garage,<br />in one account.
              </h2>
              <p className="mt-4 max-w-md text-white/80">
                Track orders, save addresses, and reorder parts in two clicks — built for owners of timeless German classics.
              </p>
              <ul className="mt-8 space-y-4 text-sm">
                <Perk icon={<Truck className="h-5 w-5" />} title="Free shipping over €99" desc="Every zone, every country — no exceptions." />
                <Perk icon={<Globe2 className="h-5 w-5" />} title="14-day returns" desc="No-questions-asked, tracked & insured." />
                <Perk icon={<Wrench className="h-5 w-5" />} title="OEM-matched parts" desc="Cross-checked against every OEM catalogue we stock." />
                <Perk icon={<ShieldCheck className="h-5 w-5" />} title="Bank-grade security" desc="Encrypted sessions & leaked-password checks." />
              </ul>
            </div>
            <p className="text-xs text-white/50">© {new Date().getFullYear()} FIXHEN. All rights reserved.</p>
          </div>
        </aside>

        {/* Form panel */}
        <main className="flex items-center justify-center p-6 sm:p-10">
          <div className="w-full max-w-md">
            <Link to="/" className="mb-8 inline-flex items-center gap-2 lg:hidden">
              <img src={logoAsset.url} alt="FIXHEN" width={32} height={32} className="h-8 w-8" />
              <span className="text-xl font-black tracking-tight text-ink">FIXHEN</span>
            </Link>

            <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="mb-6 flex gap-1 rounded-xl bg-slate-100 p-1 text-sm">
                <button
                  type="button"
                  onClick={() => setMode("signin")}
                  className={`flex-1 rounded-lg px-3 py-2 font-semibold transition ${mode === "signin" ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className={`flex-1 rounded-lg px-3 py-2 font-semibold transition ${mode === "signup" ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
                >
                  Create account
                </button>
              </div>

              <h1 className="text-2xl font-bold text-slate-900">
                {mode === "signin" ? "Welcome back" : "Create your FIXHEN account"}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                {mode === "signin"
                  ? "Sign in to track orders and reorder faster."
                  : "It takes 30 seconds. No spam, ever."}
              </p>

              <form onSubmit={submit} className="mt-6 space-y-4">
                {mode === "signup" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="fn">First name</Label>
                      <Input id="fn" autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="ln">Last name</Label>
                      <Input id="ln" autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} className="mt-1" />
                    </div>
                  </div>
                )}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="pwd">Password</Label>
                    {mode === "signin" && (
                      <button
                        type="button"
                        onClick={() => setForgotOpen(true)}
                        className="text-xs font-medium text-[#2563EB] hover:underline"
                      >
                        Forgot?
                      </button>
                    )}
                  </div>
                  <div className="relative mt-1">
                    <Input
                      id="pwd"
                      type={showPwd ? "text" : "password"}
                      autoComplete={mode === "signin" ? "current-password" : "new-password"}
                      required
                      minLength={mode === "signup" ? 8 : undefined}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      aria-label={showPwd ? "Hide password" : "Show password"}
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    >
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {mode === "signup" && password.length > 0 && (
                    <div className="mt-2">
                      <div className="flex gap-1">
                        {[0, 1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className={`h-1.5 flex-1 rounded-full ${i < score ? scoreColors[score] : "bg-slate-200"}`}
                          />
                        ))}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{scoreLabels[score]} · min 8 characters</p>
                    </div>
                  )}
                </div>

                {err && (
                  <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{err}</div>
                )}

                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] h-11 text-base font-semibold"
                >
                  {busy ? "Please wait…" : mode === "signin" ? "Sign in" : "Create account"}
                  {!busy && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>

                <p className="text-center text-xs text-slate-500">
                  By continuing you agree to our Terms & Privacy Policy.
                </p>
              </form>
            </div>

            <p className="mt-6 text-center text-sm text-slate-600">
              {mode === "signin" ? "New to FIXHEN?" : "Already have an account?"}{" "}
              <button
                type="button"
                onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
                className="font-semibold text-[#2563EB] hover:underline"
              >
                {mode === "signin" ? "Create one" : "Sign in"}
              </button>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}

function Perk({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
  return (
    <li className="flex items-start gap-3">
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/10 ring-1 ring-white/15">
        {icon}
      </div>
      <div>
        <p className="font-semibold">{title}</p>
        <p className="text-white/70">{desc}</p>
      </div>
    </li>
  );
}

function ForgotPasswordCard({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setSent(true);
    } catch (e: any) {
      setErr(e?.message ?? "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Reset your password</h1>
        <p className="mt-1 text-sm text-slate-500">
          We'll email you a secure link to set a new password.
        </p>
        {sent ? (
          <div className="mt-6 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            If an account exists for <strong>{email}</strong>, a reset link is on its way.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="rmail">Email</Label>
              <Input id="rmail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
            </div>
            {err && <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-[#2563EB] hover:bg-[#1d4ed8] h-11">
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}
        <button
          type="button"
          onClick={onBack}
          className="mt-4 text-sm text-slate-600 hover:text-slate-900"
        >
          ← Back to sign in
        </button>
      </div>
    </div>
  );
}